import { useMemo } from 'react';
import useSubscriptionStore, { FEATURES, SUBSCRIPTION_TIERS, TIER_CONFIGS } from '../stores/subscriptionStore';

/**
 * Hook for subscription and feature access management
 */
export const useSubscription = () => {
  const {
    currentTier,
    subscriptionActive,
    subscriptionExpiresAt,
    subscriptionStatus,
    currentUsage,
    entitlements,
    loading,
    error,
    initialized,
    hasFeature,
    canUseFeature,
    getEntitlementUsage,
    getTierConfig,
    getAllTierConfigs,
    getUpgradeRecommendation,
    incrementMockupUsage,
    setCurrentUsage,
    fetchSubscription,
    fetchEntitlements,
    isInGracePeriod,
    gracePeriodEndsAt,
    trialActive,
    trialExpired,
    trialDaysRemaining,
  } = useSubscriptionStore();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tierConfig = useMemo(() => getTierConfig(), [currentTier, getTierConfig]);

  const isStarterTier = useMemo(() => currentTier === SUBSCRIPTION_TIERS.STARTER, [currentTier]);
  const isGrowthTier = useMemo(() => currentTier === SUBSCRIPTION_TIERS.GROWTH, [currentTier]);
  const isProTier = useMemo(() => currentTier === SUBSCRIPTION_TIERS.PRO, [currentTier]);
  const isEnterpriseTier = useMemo(() => currentTier === SUBSCRIPTION_TIERS.ENTERPRISE, [currentTier]);

  // Legacy aliases for backward compatibility
  const isFreeTier = useMemo(() => currentTier === SUBSCRIPTION_TIERS.STARTER, [currentTier]);
  const isFullTier = useMemo(() => currentTier === SUBSCRIPTION_TIERS.PRO, [currentTier]);

  // Feature access helpers
  const canCreateMockups = useMemo(() => {
    const { mockupsThisMonth, mockupsLimit } = currentUsage;
    if (mockupsLimit === -1) return true;
    return mockupsThisMonth < mockupsLimit;
  }, [currentUsage]);

  const remainingMockups = useMemo(() => {
    const { mockupsThisMonth, mockupsLimit } = currentUsage;
    if (mockupsLimit === -1) return Infinity;
    return Math.max(0, mockupsLimit - mockupsThisMonth);
  }, [currentUsage]);

  const mockupLimit = useMemo(() => currentUsage.mockupsLimit ?? -1, [currentUsage]);

  const mockupBatchLimit = useMemo(() => currentUsage.mockupsPerBatchLimit ?? 1, [currentUsage]);

  // Template limit helpers
  const canCreateTemplate = useMemo(() => {
    const { templatesCount, templatesLimit } = currentUsage;
    return templatesLimit === -1 || templatesCount < templatesLimit;
  }, [currentUsage]);

  const remainingTemplates = useMemo(() => {
    const { templatesCount, templatesLimit } = currentUsage;
    if (templatesLimit === -1) return Infinity;
    return Math.max(0, templatesLimit - templatesCount);
  }, [currentUsage]);

  const templateLimit = useMemo(() => currentUsage.templatesLimit ?? 1, [currentUsage]);

  // Canvas config limit helpers
  const canCreateCanvas = useMemo(() => {
    const { canvasCount, canvasLimit } = currentUsage;
    return canvasLimit === -1 || canvasCount < canvasLimit;
  }, [currentUsage]);

  const remainingCanvas = useMemo(() => {
    const { canvasCount, canvasLimit } = currentUsage;
    if (canvasLimit === -1) return Infinity;
    return Math.max(0, canvasLimit - canvasCount);
  }, [currentUsage]);

  const canvasLimit = useMemo(() => currentUsage.canvasLimit ?? 1, [currentUsage]);

  // Size config limit helpers
  const canCreateSizeConfig = useMemo(() => {
    const { sizesCount, sizesLimit } = currentUsage;
    return sizesLimit === -1 || sizesCount < sizesLimit;
  }, [currentUsage]);

  const remainingSizeConfigs = useMemo(() => {
    const { sizesCount, sizesLimit } = currentUsage;
    if (sizesLimit === -1) return Infinity;
    return Math.max(0, sizesLimit - sizesCount);
  }, [currentUsage]);

  const sizeConfigLimit = useMemo(() => currentUsage.sizesLimit ?? 1, [currentUsage]);

  // Upgrade helpers
  const getUpgradeForFeature = feature => {
    return getUpgradeRecommendation(feature);
  };

  const isFeatureLocked = feature => {
    return !hasFeature(feature);
  };

  // Usage tracking — imageCount is the number of mockup images produced
  // (e.g., 3 designs uploaded = 3 images = counts as 3 toward the limit)
  const trackMockupCreation = (imageCount = 1) => {
    // Increment locally for immediate UI feedback
    incrementMockupUsage(imageCount);
    // Refresh from backend after a short delay to ensure accuracy
    setTimeout(() => {
      fetchSubscription();
    }, 1000);
  };

  const updateUsage = usageData => {
    setCurrentUsage(usageData);
  };

  // Refresh usage from backend (useful after operations that affect counts)
  const refreshUsage = () => {
    fetchSubscription();
  };

  /**
   * Pre-flight export limit check. Fetches fresh entitlements from the backend
   * and validates that the requested batch fits within the user's plan limits.
   *
   * @param {number} batchSize - Number of listings/products to create
   * @returns {Promise<{allowed: boolean, remaining: number, limit: number, used: number, tierName: string, batchLimit: number}>}
   */
  const checkExportLimit = async (batchSize = 1) => {
    // Fetch fresh entitlements to avoid stale cached data
    await fetchSubscription();

    const state = useSubscriptionStore.getState();
    const limits = state.entitlements?.limits || {};
    const usage = state.entitlements?.usage || {};
    const tier = state.currentTier;
    const config = TIER_CONFIGS[tier] || TIER_CONFIGS[SUBSCRIPTION_TIERS.STARTER];

    const monthlyLimit = limits.exports_per_month ?? config.limits.exportsPerMonth ?? 0;
    const currentUsed = usage.exports_per_month ?? state.currentUsage.mockupsThisMonth ?? 0;
    const batchLimit = limits.mockups_per_batch ?? config.limits.mockupsPerBatch ?? 1;
    const remaining = monthlyLimit === -1 ? Infinity : Math.max(0, monthlyLimit - currentUsed);

    const problems = [];

    if (monthlyLimit !== -1 && currentUsed + batchSize > monthlyLimit) {
      problems.push(
        `This upload of ${batchSize} listing(s) would exceed your monthly limit of ${monthlyLimit}. ` +
          `You have ${remaining} export(s) remaining this month.`
      );
    }

    if (batchLimit !== -1 && batchSize > batchLimit) {
      problems.push(`Your plan allows ${batchLimit} listing(s) per batch, but you are trying to upload ${batchSize}.`);
    }

    return {
      allowed: problems.length === 0,
      problems,
      remaining,
      limit: monthlyLimit,
      used: currentUsed,
      batchLimit,
      tierName: config.name,
    };
  };

  return {
    // Subscription state
    currentTier,
    tierConfig,
    subscriptionActive,
    subscriptionStatus,
    subscriptionExpiresAt,
    loading,
    error,
    initialized,

    // Grace period (shown as warning banner; access is still granted during grace)
    isInGracePeriod,
    gracePeriodEndsAt,

    // Trial state
    trialActive,
    trialExpired,
    trialDaysRemaining,

    // Entitlements
    entitlements,

    // Tier checks
    isStarterTier,
    isGrowthTier,
    isProTier,
    isEnterpriseTier,
    // Legacy aliases
    isFreeTier,
    isFullTier,

    // Usage state
    currentUsage,
    remainingMockups,
    mockupLimit,
    mockupBatchLimit,

    // Template limits
    canCreateTemplate,
    remainingTemplates,
    templateLimit,

    // Canvas config limits
    canCreateCanvas,
    remainingCanvas,
    canvasLimit,

    // Size config limits
    canCreateSizeConfig,
    remainingSizeConfigs,
    sizeConfigLimit,

    // Feature access
    hasFeature,
    canUseFeature,
    canCreateMockups,
    isFeatureLocked,

    // Configuration helpers
    getTierConfig,
    getAllTierConfigs,
    getEntitlementUsage,

    // Upgrade helpers
    getUpgradeForFeature,

    // Usage tracking
    trackMockupCreation,
    updateUsage,
    refreshUsage,

    // Pre-flight limit check
    checkExportLimit,

    // Fetch subscription from backend
    fetchSubscription,
    fetchEntitlements,

    // Constants for components
    FEATURES,
    SUBSCRIPTION_TIERS,
  };
};

/**
 * Hook for checking if a specific feature is available
 */
export const useFeatureAccess = feature => {
  const { hasFeature, canUseFeature, getUpgradeRecommendation } = useSubscriptionStore();

  return {
    hasAccess: hasFeature(feature),
    canUse: canUseFeature(feature),
    upgradeInfo: getUpgradeRecommendation(feature),
  };
};

/**
 * Hook for feature gates - returns props for feature restriction components
 */
export const useFeatureGate = (feature, options = {}) => {
  const { hasFeature, canUseFeature, getUpgradeRecommendation, currentTier } = useSubscriptionStore();

  const hasAccess = hasFeature(feature);
  const canUse = canUseFeature(feature);
  const upgradeInfo = getUpgradeRecommendation(feature);

  const { showUpgradePrompt = true, blockInteraction = true, customMessage } = options;

  return {
    // State
    isLocked: !hasAccess,
    isDisabled: !canUse,
    hasAccess,
    canUse,

    // Upgrade info
    upgradeInfo,
    shouldShowUpgrade: showUpgradePrompt && !hasAccess,

    // Interaction control
    onClick: blockInteraction && !canUse ? e => e.preventDefault() : undefined,
    className: !canUse ? 'opacity-50 cursor-not-allowed' : '',

    // Messages
    lockMessage: customMessage || `This feature requires ${upgradeInfo?.config.name || 'a higher tier'}`,

    // Current tier
    currentTier,
  };
};

export default useSubscription;
